import { alpha, Box, Button, Grid, Table, TableBody, TableCell, TableContainer, TableFooter, TableHead, TablePagination, TableRow } from '@mui/material';
import 'jspdf-autotable';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { MainBorderRadius, TableHeader } from '../../../app/AppStyles';
import { DisplayName, GetDate, GetTime } from '../../../app/AppUtils';
import AutocompleteSelector from '../../subcomponents/AutocompleteSelector';
import ContentLoader from '../../subcomponents/ContentLoader';
import TablePaginationActions from '../../subcomponents/TablePaginationActions';
import { fetchReportActivities, fetchReportPerformances, resetReportFilterValues } from '../reportFiltersSlice';
import ExportExcelOpenPayments from './ExportExcelOpenPayments';
import { fetchOpenPaymentsReport } from './reportWintersportSlice';

export default function ReportOpenPaymentsTab() {
    const dispatch = useDispatch();

    const [searching, setSearching] = useState(false);
    const [selectedActivity, setSelectedActivity] = useState();
    const [selectedPerformances, setSelectedPerformances] = useState();

    const { appLanguage, resourceFinal } = useSelector((state) => state.settings.resource.resources);
    const { openPaymentsReport, openPaymentsReportStatus } = useSelector((state) => state.reports.wintersport);
    const { reportActivities, reportActivitiesStatus, reportPerformances, reportPerformancesStatus } = useSelector((state) => state.reports.reportFilters);

    useEffect(() => {
        dispatch(resetReportFilterValues());
        dispatch(fetchReportActivities());
    }, [dispatch]);

    useEffect(() => {
        if (selectedActivity)
            dispatch(fetchReportPerformances({ activityId: selectedActivity.activityId, key: "subscriptions" }));
    }, [dispatch, selectedActivity]);

    useEffect(() => {
        if (openPaymentsReportStatus === "success" || openPaymentsReportStatus === "failed")
            setSearching(false);
    }, [openPaymentsReportStatus]);

    const handleSearchReportClick = () => {
        setSearching(true);
        dispatch(fetchOpenPaymentsReport(selectedPerformances.map(x => x.performanceId)));
    };

    const TableFragment = () => {
        const [page, setPage] = useState(0);
        const [rowsPerPage, setRowsPerPage] = useState(openPaymentsReport?.length < 100 ? -1 : 100);

        const emptyRows = page > 0 ? Math.max(0, (1 + page) * rowsPerPage - openPaymentsReport?.length) : 0;

        const handleChangePage = (event, newPage) => {
            setPage(newPage);
        };

        const handleChangeRowsPerPage = (event) => {
            setRowsPerPage(parseInt(event.target.value, 10));
            setPage(0);
        };

        return (
            <>
                <TableBody>
                    {(rowsPerPage > 0 ? openPaymentsReport?.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage) : openPaymentsReport)?.map((item, index) =>
                        <TableRow key={index} sx={{ '&:nth-of-type(odd)': { bgcolor: 'white.main' }, height: 63 }}>
                            <TableCell align="left">{item.activityName}</TableCell>
                            <TableCell align="left">{item.performanceName}</TableCell>
                            <TableCell align="center">{GetDate(item.starttime)} ({GetTime(item.starttime)})</TableCell>
                            <TableCell align="center">{item.privateEmail}</TableCell>
                            <TableCell align="center">{item.privatePhone}</TableCell>
                            <TableCell align="center">{item.number}</TableCell>
                            <TableCell align="center">{DisplayName(item.firstname, item.insertion, item.lastname)}</TableCell>
                        </TableRow>
                    )}
                    {(rowsPerPage > 0 ? openPaymentsReport?.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage) : openPaymentsReport)?.length === 0 && <TableRow><TableCell colSpan={13}>{resourceFinal[appLanguage].messages.FILTER_RESULT_EMPTY}</TableCell></TableRow>}
                    {emptyRows > 0 && <TableRow style={{ height: 63 * emptyRows }}><TableCell colSpan={13} /></TableRow>}
                </TableBody>
                <TableFooter>
                    <TableRow>
                        <TablePagination
                            sx={{ border: 0 }}
                            rowsPerPageOptions={[10, 25, 50,
                                {
                                    label: openPaymentsReport?.length < 100 ? resourceFinal[appLanguage].words.ALL : '100',
                                    value: openPaymentsReport?.length < 100 ? -1 : 100
                                }
                            ]}
                            count={openPaymentsReport ? openPaymentsReport?.length : 0}
                            rowsPerPage={rowsPerPage}
                            page={page}
                            onPageChange={handleChangePage}
                            onRowsPerPageChange={handleChangeRowsPerPage}
                            ActionsComponent={TablePaginationActions}
                            labelDisplayedRows={({ from, to, count }) => { return `${from}-${to} ${resourceFinal[appLanguage].messages.OF_THE} ${count}`; }}
                            labelRowsPerPage={resourceFinal[appLanguage].messages.TABLE_ROWS_PER_PAGE}
                        />
                    </TableRow>
                </TableFooter>
            </>
        );
    };

    return (
        <>
            <Box sx={{ display: 'flex', flexDirection: 'column', flex: '1 1 auto' }}>
                <Grid container direction="row" spacing={1} sx={{ bgcolor: theme => alpha(theme.palette.white.main, 0.3), borderRadius: MainBorderRadius, p: 1 }}>
                    <Grid size={3}>
                        <AutocompleteSelector
                            label={resourceFinal[appLanguage].words.ACTIVITY}
                            options={reportActivities ?? []}
                            value={selectedActivity ?? null}
                            onChange={(val) => {
                                setSelectedPerformances([]);
                                setSelectedActivity(val)
                            }}
                            getOptionLabel={(option) => `${option.activityName} (${option.performancesSubscriptionCount})`}
                            getOptionKey={(option) => option.activityId}
                            isOptionEqualToValue={(option, value) => option.activityId === value?.activityId}
                            disabled={reportActivitiesStatus !== "success"}
                        />
                    </Grid>
                    <Grid size={3}>
                        <AutocompleteSelector
                            label={resourceFinal[appLanguage].words.PERFORMANCE}
                            options={reportPerformances ?? []}
                            value={selectedPerformances ?? []}
                            onChange={(val) => {
                                setSelectedPerformances(val)
                            }}
                            getOptionLabel={(option) => option.performanceName}
                            getOptionKey={(option) => option.performanceId}
                            isOptionEqualToValue={(option, value) => option.performanceId === value?.performanceId}
                            disabled={reportPerformancesStatus !== "success"}
                            multiple
                        />
                    </Grid>
                    <Grid size={6}>
                        <Button
                            variant="outlined"
                            color="secondary"
                            disabled={!selectedPerformances?.length > 0}
                            sx={{ mr: 1, height: 1 }}
                            onClick={handleSearchReportClick}
                        >
                            {resourceFinal[appLanguage].words.REPORT_GENERATE}
                        </Button>
                        <ExportExcelOpenPayments disabled={searching || !openPaymentsReport} jsonData={openPaymentsReport} />
                    </Grid>
                </Grid>
                <ContentLoader
                    successCondition={searching === false}
                    errorCondition={openPaymentsReport === "failed"}
                    content={
                        <TableContainer>
                            <Table size="small" id="#report">
                                <TableHead>
                                    <TableRow>
                                        <TableCell align="left" sx={{ ...TableHeader }}>{resourceFinal[appLanguage].words.ACTIVITY}</TableCell>
                                        <TableCell align="left" sx={{ ...TableHeader }}>{resourceFinal[appLanguage].words.PERFORMANCE}</TableCell>
                                        <TableCell align="center" sx={{ ...TableHeader }}>{resourceFinal[appLanguage].words.START}{resourceFinal[appLanguage].words.TIME.toLowerCase()}</TableCell>
                                        <TableCell align="center" sx={{ ...TableHeader }}>{resourceFinal[appLanguage].words.STUDENT}{resourceFinal[appLanguage].words.EMAIL.toLowerCase()}</TableCell>
                                        <TableCell align="center" sx={{ ...TableHeader }}>{resourceFinal[appLanguage].words.STUDENT}{resourceFinal[appLanguage].words.PHONENUMBER.toLowerCase()}</TableCell>
                                        <TableCell align="center" sx={{ ...TableHeader }}>{resourceFinal[appLanguage].words.STUDENT}{resourceFinal[appLanguage].words.NUMBER.toLowerCase()}</TableCell>
                                        <TableCell align="center" sx={{ ...TableHeader }}>{resourceFinal[appLanguage].words.STUDENT}</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableFragment />
                            </Table>
                        </TableContainer>
                    }
                />
            </Box>
        </>
    );
}
